import React, { createContext, useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { ROLE_SUPER_ADMIN } from '../constants/role';

export const UserDetailsContext = createContext();
// Normalize both old and new slugs so that the old app knows how to resolve pages
const normalizePageList = (pageList) => {
  if (!Array.isArray(pageList)) return [];
  const expanded = [...pageList];
  
  const NEW_TO_OLD = {
    'blue-card': 'bluecard',
    'blue-card-report': 'bluecardreport',
    'oee-dashboard': 'deviceoee',
    'quality-board': 'quality',
    'machine-group': 'machines-group',
    'group-registration': 'group',
    'erp-report': 'erpreport',
    'email-configuration': 'erpreportschedule',
    'notifications': 'notification-center'
  };
  const OLD_TO_NEW = {
    'bluecard': 'blue-card',
    'bluecardreport': 'blue-card-report',
    'deviceoee': 'oee-dashboard',
    'quality': 'quality-board',
    'machines-group': 'machine-group',
    'group': 'group-registration',
    'reason-group': 'group-registration',
    'erpreport': 'erp-report',
    'erpreportschedule': 'email-configuration',
    'notification-center': 'notifications',
    'operator-registration': 'user-registration'
  };
  pageList.forEach(s => {
    if (NEW_TO_OLD[s]) expanded.push(NEW_TO_OLD[s]);
    if (OLD_TO_NEW[s]) expanded.push(OLD_TO_NEW[s]);
  });
  return Array.from(new Set(expanded));
};
export const UserDetailsProvider = ({ children }) => {
  const [userDetails, setUserDetails] = useState(() => {
    const raw = localStorage.getItem('userDetails');
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.pageList) {
        parsed.pageList = normalizePageList(parsed.pageList);
      }
      return parsed;
    } catch {
      return {};
    }
  });
  // Re-identify user on page refresh if already logged in
  useEffect(() => {
    const customerId = JSON.parse(localStorage.getItem('CustomerID'));
    const customerTitle = localStorage.getItem('customerTitle');
    const email = localStorage.getItem('email');
    if (!customerId) return;
    const env = window._env_ || {};
    Sentry.setUser({
      id: customerId,
      email,
      customer: customerTitle || 'Unknown',
      environment: window.location.hostname === 'smart.yantra24x7.com' ? 'production' : 'development',
    });
    posthog.identify(customerId, {
      email,
      customer: customerTitle || 'Unknown',
      environment: window.location.hostname === 'smart.yantra24x7.com' ? 'production' : 'development',
    });
  }, []);
  const updateUserDetails = (data) => {
    try {
      let parsedData = typeof data === 'string' ? JSON.parse(data) : { ...data };
      if (!Array.isArray(parsedData.pageList)) {
        parsedData.pageList = [];
      }
      parsedData.pageList = normalizePageList(parsedData.pageList);
      if (!parsedData.pageList.includes('notification-center')) {
        parsedData.pageList.push('notification-center');
      }
      if (parsedData.mode === ROLE_SUPER_ADMIN && !parsedData.pageList.includes('insights')) {
        parsedData.pageList.push('insights');
      }
      localStorage.setItem('userDetails', JSON.stringify(parsedData));
      setUserDetails(parsedData);
    } catch (error) {
      console.error('Failed to update user details:', error);
    }
  };

  const logout = () => {
    posthog.reset();
    Sentry.setUser(null);
    localStorage.removeItem('userDetails');
    setUserDetails({});
  };

  return (
    <UserDetailsContext.Provider value={{ userDetails, updateUserDetails, logout }}>
      {children}
    </UserDetailsContext.Provider>
  );
};
